import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Nav,
  Alert,
  Spinner,
} from "react-bootstrap";
import "./Profile.css";

// Constants
const PROFILE_TABS = {
  PERSONAL: "personal",
  SECURITY: "security",
  MEMBERSHIP: "membership",
  BOOKING_HISTORY: "bookingHistory",
};

const MEMBERSHIP_TIERS = {
  BRONZE: { name: "Bronze", color: "#CD7F32", minPoints: 0 },
  SILVER: { name: "Silver", color: "#C0C0C0", minPoints: 500 },
  GOLD: { name: "Gold", color: "#FFD700", minPoints: 1000 },
  PLATINUM: { name: "Platinum", color: "#E5E4E2", minPoints: 2000 },
};

/**
 * Profile Component - UC03: Quản lý hồ sơ cá nhân
 * Customer cập nhật thông tin liên lạc và xem hạng thành viên
 */
function Profile() {
  // State management - following React best practices (useState for local state)
  const [activeTab, setActiveTab] = useState(PROFILE_TABS.PERSONAL);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [errors, setErrors] = useState({});

  // Form data state
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    dateOfBirth: "",
    address: "",
    city: "",
    avatar: "",
  });

  // Original data for cancel functionality
  const [originalData, setOriginalData] = useState({});

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Membership data
  const [membershipData, setMembershipData] = useState({
    tier: "BRONZE",
    points: 0,
    joinDate: "",
    totalSpent: 0,
  });

  // Booking history data
  const [bookingHistory, setBookingHistory] = useState([]);

  // Load user data on component mount - useEffect for side effects
  useEffect(() => {
    loadUserProfile();
  }, []);

  // Auto-dismiss messages after 5 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: "", text: "" });
      }, 5000);
      return () => clearTimeout(timer); // Cleanup function to prevent memory leaks
    }
  }, [message]);

  /**
   * Load user profile from localStorage/API
   * In production, this would fetch from API endpoint
   */
  const loadUserProfile = () => {
    setIsLoading(true);

    // Simulate API call with setTimeout
    setTimeout(() => {
      const userData = {
        fullName: localStorage.getItem("userName") || "Nguyễn Văn A",
        email: localStorage.getItem("userEmail") || "user@example.com",
        phone: "0901234567",
        dateOfBirth: "1995-06-15",
        address: "123 Nguyễn Huệ",
        city: "Hồ Chí Minh",
        avatar: "/images/default-avatar.png",
      };

      const membership = {
        tier: "SILVER",
        points: 750,
        joinDate: "2023-01-15",
        totalSpent: 5500000,
      };

      const history = [
        {
          id: "BK001",
          date: "2025-01-10",
          table: "Bàn 03",
          duration: "2 giờ",
          amount: 100000,
          status: "completed",
        },
        {
          id: "BK002",
          date: "2025-01-05",
          table: "Quiet 1",
          duration: "4 giờ",
          amount: 240000,
          status: "completed",
        },
        {
          id: "BK003",
          date: "2024-12-28",
          table: "Meeting A",
          duration: "2 giờ",
          amount: 400000,
          status: "cancelled",
        },
      ];

      setFormData(userData);
      setOriginalData(userData);
      setMembershipData(membership);
      setBookingHistory(history);
      setIsLoading(false);
    }, 800);
  };

  /**
   * Handle input changes with validation
   * Validates on blur for better UX (following ux-guidelines.csv)
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  /**
   * Validate form fields
   * Returns true if all fields are valid
   */
  const validateForm = () => {
    const newErrors = {};

    // Full name validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Họ và tên không được để trống";
    } else if (formData.fullName.trim().length < 3) {
      newErrors.fullName = "Họ và tên phải có ít nhất 3 ký tự";
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = "Email không được để trống";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Email không hợp lệ";
    }

    // Phone validation
    const phoneRegex = /^[0-9]{10}$/;
    if (!formData.phone.trim()) {
      newErrors.phone = "Số điện thoại không được để trống";
    } else if (!phoneRegex.test(formData.phone.replace(/\s/g, ""))) {
      newErrors.phone = "Số điện thoại phải có 10 chữ số";
    }

    // Date of birth validation
    if (formData.dateOfBirth) {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      if (age < 13) {
        newErrors.dateOfBirth = "Bạn phải từ 13 tuổi trở lên";
      } else if (age > 120) {
        newErrors.dateOfBirth = "Ngày sinh không hợp lệ";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle save profile
   * Following ux-guidelines: Show loading -> Success/Error feedback
   */
  const handleSaveProfile = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setMessage({
        type: "danger",
        text: "Vui lòng kiểm tra lại thông tin!",
      });
      return;
    }

    setIsSaving(true);

    // Simulate API call
    setTimeout(() => {
      // Update localStorage
      localStorage.setItem("userName", formData.fullName);
      localStorage.setItem("userEmail", formData.email);

      setOriginalData(formData);
      setIsEditMode(false);
      setIsSaving(false);
      setMessage({
        type: "success",
        text: "Cập nhật thông tin thành công!",
      });
    }, 1000);
  };

  /**
   * Handle cancel edit mode
   * Revert to original data
   */
  const handleCancelEdit = () => {
    setFormData(originalData);
    setErrors({});
    setIsEditMode(false);
    setMessage({ type: "", text: "" });
  };

  /**
   * Handle password change
   */
  const handlePasswordChange = (e) => {
    e.preventDefault();

    const newErrors = {};

    if (!passwordData.currentPassword) {
      newErrors.currentPassword = "Vui lòng nhập mật khẩu hiện tại";
    }

    if (!passwordData.newPassword) {
      newErrors.newPassword = "Vui lòng nhập mật khẩu mới";
    } else if (passwordData.newPassword.length < 6) {
      newErrors.newPassword = "Mật khẩu phải có ít nhất 6 ký tự";
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = "Mật khẩu xác nhận không khớp";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSaving(true);

    // Simulate API call
    setTimeout(() => {
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setIsSaving(false);
      setMessage({
        type: "success",
        text: "Đổi mật khẩu thành công!",
      });
    }, 1000);
  };

  /**
   * Get membership tier info based on points
   */
  const getMembershipTier = (points) => {
    if (points >= MEMBERSHIP_TIERS.PLATINUM.minPoints)
      return MEMBERSHIP_TIERS.PLATINUM;
    if (points >= MEMBERSHIP_TIERS.GOLD.minPoints) return MEMBERSHIP_TIERS.GOLD;
    if (points >= MEMBERSHIP_TIERS.SILVER.minPoints)
      return MEMBERSHIP_TIERS.SILVER;
    return MEMBERSHIP_TIERS.BRONZE;
  };

  /**
   * Calculate progress to next tier
   */
  const getNextTierProgress = (points) => {
    const tiers = [
      { name: "SILVER", min: 500 },
      { name: "GOLD", min: 1000 },
      { name: "PLATINUM", min: 2000 },
    ];

    for (const tier of tiers) {
      if (points < tier.min) {
        const prevMin = tiers[tiers.indexOf(tier) - 1]?.min || 0;
        const progress = ((points - prevMin) / (tier.min - prevMin)) * 100;
        return {
          nextTier: tier.name,
          progress: Math.min(progress, 100),
          pointsNeeded: tier.min - points,
        };
      }
    }

    return { nextTier: "MAX", progress: 100, pointsNeeded: 0 };
  };

  // Render loading state
  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <Spinner animation="border" variant="primary" role="status">
          <span className="visually-hidden">Đang tải...</span>
        </Spinner>
      </div>
    );
  }

  const currentTier = getMembershipTier(membershipData.points);
  const tierProgress = getNextTierProgress(membershipData.points);

  return (
    <div className="profile-page" id="main-content">
      {/* Header Section */}
      <div className="profile-header">
        <Container>
          <Row className="align-items-center">
            <Col md={2} className="text-center">
              <div className="avatar-container">
                <img
                  src={formData.avatar || "/images/default-avatar.png"}
                  alt="Avatar"
                  className="profile-avatar"
                  onError={(e) => {
                    e.target.src = "https://via.placeholder.com/120";
                  }}
                />
                {isEditMode && (
                  <Button
                    variant="light"
                    size="sm"
                    className="avatar-upload-btn"
                    aria-label="Thay đổi ảnh đại diện"
                  >
                    <i className="bi bi-camera"></i>
                  </Button>
                )}
              </div>
            </Col>
            <Col md={7}>
              <h2 className="profile-name">{formData.fullName}</h2>
              <p className="profile-email">
                <i className="bi bi-envelope me-2"></i>
                {formData.email}
              </p>
              <div
                className="membership-badge"
                style={{ backgroundColor: currentTier.color }}
              >
                <i className="bi bi-award me-2"></i>
                {currentTier.name} Member
              </div>
            </Col>
            <Col md={3} className="text-end">
              {!isEditMode && activeTab === PROFILE_TABS.PERSONAL && (
                <Button
                  variant="primary"
                  onClick={() => setIsEditMode(true)}
                  className="btn-edit-profile"
                >
                  <i className="bi bi-pencil me-2"></i>
                  Chỉnh sửa hồ sơ
                </Button>
              )}
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="py-5">
        {/* Alert Messages */}
        {message.text && (
          <Alert
            variant={message.type}
            dismissible
            onClose={() => setMessage({ type: "", text: "" })}
            className="mb-4"
          >
            <i
              className={`bi bi-${
                message.type === "success"
                  ? "check-circle"
                  : "exclamation-triangle"
              } me-2`}
            ></i>
            {message.text}
          </Alert>
        )}

        <Row>
          {/* Sidebar Navigation */}
          <Col md={3}>
            <Card
              className="shadow-sm border-0 rounded-4 sticky-top"
              style={{ top: "100px" }}
            >
              <Card.Body className="p-0">
                <Nav variant="pills" className="flex-column profile-nav">
                  <Nav.Item>
                    <Nav.Link
                      active={activeTab === PROFILE_TABS.PERSONAL}
                      onClick={() => {
                        setActiveTab(PROFILE_TABS.PERSONAL);
                        setIsEditMode(false);
                        setErrors({});
                      }}
                      className="d-flex align-items-center"
                    >
                      <i className="bi bi-person me-2"></i>
                      Thông tin cá nhân
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      active={activeTab === PROFILE_TABS.SECURITY}
                      onClick={() => {
                        setActiveTab(PROFILE_TABS.SECURITY);
                        setIsEditMode(false);
                        setErrors({});
                      }}
                      className="d-flex align-items-center"
                    >
                      <i className="bi bi-shield-lock me-2"></i>
                      Bảo mật
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      active={activeTab === PROFILE_TABS.MEMBERSHIP}
                      onClick={() => {
                        setActiveTab(PROFILE_TABS.MEMBERSHIP);
                        setIsEditMode(false);
                        setErrors({});
                      }}
                      className="d-flex align-items-center"
                    >
                      <i className="bi bi-award me-2"></i>
                      Hạng thành viên
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      active={activeTab === PROFILE_TABS.BOOKING_HISTORY}
                      onClick={() => {
                        setActiveTab(PROFILE_TABS.BOOKING_HISTORY);
                        setIsEditMode(false);
                        setErrors({});
                      }}
                      className="d-flex align-items-center"
                    >
                      <i className="bi bi-clock-history me-2"></i>
                      Lịch sử đặt chỗ
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Card.Body>
            </Card>
          </Col>

          {/* Main Content */}
          <Col md={9}>
            {/* Personal Information Tab */}
            {activeTab === PROFILE_TABS.PERSONAL && (
              <Card className="shadow-sm border-0 rounded-4">
                <Card.Header className="bg-white border-0 p-4">
                  <h4 className="mb-0 fw-bold">
                    <i className="bi bi-person-lines-fill me-2 text-primary"></i>
                    Thông tin cá nhân
                  </h4>
                </Card.Header>
                <Card.Body className="p-4">
                  <Form onSubmit={handleSaveProfile}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label htmlFor="fullName">
                            Họ và tên <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            id="fullName"
                            type="text"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleInputChange}
                            disabled={!isEditMode}
                            isInvalid={!!errors.fullName}
                            className="rounded-3"
                            autoComplete="name"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.fullName}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label htmlFor="email">
                            Email <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            id="email"
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            disabled={!isEditMode}
                            isInvalid={!!errors.email}
                            className="rounded-3"
                            autoComplete="email"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.email}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label htmlFor="phone">
                            Số điện thoại <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            id="phone"
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            disabled={!isEditMode}
                            isInvalid={!!errors.phone}
                            className="rounded-3"
                            autoComplete="tel"
                            inputMode="numeric"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.phone}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label htmlFor="dateOfBirth">
                            Ngày sinh
                          </Form.Label>
                          <Form.Control
                            id="dateOfBirth"
                            type="date"
                            name="dateOfBirth"
                            value={formData.dateOfBirth}
                            onChange={handleInputChange}
                            disabled={!isEditMode}
                            isInvalid={!!errors.dateOfBirth}
                            className="rounded-3"
                            autoComplete="bday"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.dateOfBirth}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={8}>
                        <Form.Group className="mb-3">
                          <Form.Label htmlFor="address">Địa chỉ</Form.Label>
                          <Form.Control
                            id="address"
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleInputChange}
                            disabled={!isEditMode}
                            className="rounded-3"
                            autoComplete="street-address"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label htmlFor="city">Thành phố</Form.Label>
                          <Form.Control
                            id="city"
                            type="text"
                            name="city"
                            value={formData.city}
                            onChange={handleInputChange}
                            disabled={!isEditMode}
                            className="rounded-3"
                            autoComplete="address-level1"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* Action Buttons - Only show in edit mode */}
                    {isEditMode && (
                      <div className="d-flex justify-content-end gap-2 mt-4">
                        <Button
                          variant="light"
                          onClick={handleCancelEdit}
                          disabled={isSaving}
                          className="px-4 rounded-3"
                        >
                          <i className="bi bi-x-circle me-2"></i>
                          Hủy
                        </Button>
                        <Button
                          variant="primary"
                          type="submit"
                          disabled={isSaving}
                          className="px-4 rounded-3"
                        >
                          {isSaving ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                              />
                              Đang lưu...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-check-circle me-2"></i>
                              Lưu thay đổi
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </Form>
                </Card.Body>
              </Card>
            )}

            {/* Security Tab */}
            {activeTab === PROFILE_TABS.SECURITY && (
              <Card className="shadow-sm border-0 rounded-4">
                <Card.Header className="bg-white border-0 p-4">
                  <h4 className="mb-0 fw-bold">
                    <i className="bi bi-shield-lock-fill me-2 text-primary"></i>
                    Bảo mật tài khoản
                  </h4>
                </Card.Header>
                <Card.Body className="p-4">
                  <Form onSubmit={handlePasswordChange}>
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label htmlFor="currentPassword">
                            Mật khẩu hiện tại{" "}
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            id="currentPassword"
                            type="password"
                            value={passwordData.currentPassword}
                            onChange={(e) =>
                              setPasswordData((prev) => ({
                                ...prev,
                                currentPassword: e.target.value,
                              }))
                            }
                            isInvalid={!!errors.currentPassword}
                            className="rounded-3"
                            autoComplete="current-password"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.currentPassword}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label htmlFor="newPassword">
                            Mật khẩu mới <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            id="newPassword"
                            type="password"
                            value={passwordData.newPassword}
                            onChange={(e) =>
                              setPasswordData((prev) => ({
                                ...prev,
                                newPassword: e.target.value,
                              }))
                            }
                            isInvalid={!!errors.newPassword}
                            className="rounded-3"
                            autoComplete="new-password"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.newPassword}
                          </Form.Control.Feedback>
                          <Form.Text className="text-muted">
                            <i className="bi bi-info-circle me-1"></i>
                            Mật khẩu phải có ít nhất 6 ký tự
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label htmlFor="confirmPassword">
                            Xác nhận mật khẩu{" "}
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            id="confirmPassword"
                            type="password"
                            value={passwordData.confirmPassword}
                            onChange={(e) =>
                              setPasswordData((prev) => ({
                                ...prev,
                                confirmPassword: e.target.value,
                              }))
                            }
                            isInvalid={!!errors.confirmPassword}
                            className="rounded-3"
                            autoComplete="new-password"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.confirmPassword}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-end mt-4">
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={isSaving}
                        className="px-4 rounded-3"
                      >
                        {isSaving ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Đang cập nhật...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-key me-2"></i>
                            Đổi mật khẩu
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            )}

            {/* Membership Tab */}
            {activeTab === PROFILE_TABS.MEMBERSHIP && (
              <Card className="shadow-sm border-0 rounded-4">
                <Card.Header className="bg-white border-0 p-4">
                  <h4 className="mb-0 fw-bold">
                    <i className="bi bi-award-fill me-2 text-primary"></i>
                    Hạng thành viên
                  </h4>
                </Card.Header>
                <Card.Body className="p-4">
                  {/* Current Tier */}
                  <div className="text-center mb-4">
                    <div
                      className="membership-tier-badge"
                      style={{
                        backgroundColor: currentTier.color,
                        padding: "30px",
                        borderRadius: "20px",
                        color: "#fff",
                        textShadow: "0 2px 4px rgba(0,0,0,0.2)",
                      }}
                    >
                      <i className="bi bi-trophy display-1"></i>
                      <h2 className="mt-3 mb-0">{currentTier.name} Member</h2>
                      <p className="mb-0 opacity-75">Hạng hiện tại của bạn</p>
                    </div>
                  </div>

                  {/* Points & Progress */}
                  <Row className="text-center mb-4">
                    <Col md={4}>
                      <div className="stat-card p-3 rounded-3 bg-light">
                        <i className="bi bi-star-fill text-warning fs-2"></i>
                        <h3 className="mt-2 mb-0">{membershipData.points}</h3>
                        <small className="text-muted">Điểm tích lũy</small>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="stat-card p-3 rounded-3 bg-light">
                        <i className="bi bi-calendar-check text-success fs-2"></i>
                        <h3 className="mt-2 mb-0">
                          {new Date(membershipData.joinDate).toLocaleDateString(
                            "vi-VN"
                          )}
                        </h3>
                        <small className="text-muted">Ngày tham gia</small>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="stat-card p-3 rounded-3 bg-light">
                        <i className="bi bi-cash-coin text-primary fs-2"></i>
                        <h3 className="mt-2 mb-0">
                          {new Intl.NumberFormat("vi-VN", {
                            style: "currency",
                            currency: "VND",
                          }).format(membershipData.totalSpent)}
                        </h3>
                        <small className="text-muted">Tổng chi tiêu</small>
                      </div>
                    </Col>
                  </Row>

                  {/* Progress to Next Tier */}
                  {tierProgress.nextTier !== "MAX" && (
                    <div className="mb-4">
                      <h5 className="mb-3">
                        Tiến độ lên hạng {tierProgress.nextTier}
                      </h5>
                      <div
                        className="progress"
                        style={{ height: "30px", borderRadius: "15px" }}
                      >
                        <div
                          className="progress-bar progress-bar-striped progress-bar-animated"
                          role="progressbar"
                          aria-valuenow={tierProgress.progress}
                          aria-valuemin="0"
                          aria-valuemax="100"
                          style={{ width: `${tierProgress.progress}%` }}
                        >
                          {Math.round(tierProgress.progress)}%
                        </div>
                      </div>
                      <p className="text-muted mt-2 mb-0">
                        <i className="bi bi-info-circle me-1"></i>
                        Còn thiếu {tierProgress.pointsNeeded} điểm để lên hạng{" "}
                        {tierProgress.nextTier}
                      </p>
                    </div>
                  )}

                  {/* Tier Benefits */}
                  <hr />
                  <h5 className="mb-3">Quyền lợi thành viên</h5>
                  <Row>
                    {Object.values(MEMBERSHIP_TIERS).map((tier) => (
                      <Col md={6} key={tier.name} className="mb-3">
                        <Card
                          className={`border-2 ${
                            currentTier.name === tier.name
                              ? "border-primary"
                              : "border-light"
                          }`}
                          style={{
                            backgroundColor:
                              currentTier.name === tier.name
                                ? "rgba(40, 167, 69, 0.05)"
                                : "white",
                          }}
                        >
                          <Card.Body>
                            <div className="d-flex align-items-center mb-2">
                              <div
                                className="rounded-circle me-2"
                                style={{
                                  width: "30px",
                                  height: "30px",
                                  backgroundColor: tier.color,
                                }}
                              ></div>
                              <h6 className="mb-0 fw-bold">{tier.name}</h6>
                              {currentTier.name === tier.name && (
                                <span className="badge bg-success ms-auto">
                                  Hiện tại
                                </span>
                              )}
                            </div>
                            <small className="text-muted">
                              Từ {tier.minPoints} điểm
                            </small>
                            <ul className="mt-2 mb-0 small">
                              <li>
                                Giảm{" "}
                                {tier.name === "BRONZE"
                                  ? "5"
                                  : tier.name === "SILVER"
                                  ? "10"
                                  : tier.name === "GOLD"
                                  ? "15"
                                  : "20"}
                                % tất cả dịch vụ
                              </li>
                              <li>
                                Tích điểm x
                                {tier.name === "BRONZE"
                                  ? "1"
                                  : tier.name === "SILVER"
                                  ? "1.2"
                                  : tier.name === "GOLD"
                                  ? "1.5"
                                  : "2"}
                              </li>
                              {tier.name !== "BRONZE" && (
                                <li>Ưu tiên đặt chỗ</li>
                              )}
                              {tier.name === "PLATINUM" && (
                                <li>Phòng VIP miễn phí 1h/tuần</li>
                              )}
                            </ul>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Card.Body>
              </Card>
            )}

            {/* Booking History Tab */}
            {activeTab === PROFILE_TABS.BOOKING_HISTORY && (
              <Card className="shadow-sm border-0 rounded-4">
                <Card.Header className="bg-white border-0 p-4">
                  <h4 className="mb-0 fw-bold">
                    <i className="bi bi-clock-history me-2 text-primary"></i>
                    Lịch sử đặt chỗ
                  </h4>
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="px-4 py-3">Mã đặt chỗ</th>
                          <th className="py-3">Ngày</th>
                          <th className="py-3">Bàn</th>
                          <th className="py-3">Thời lượng</th>
                          <th className="py-3">Số tiền</th>
                          <th className="py-3">Trạng thái</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookingHistory.length > 0 ? (
                          bookingHistory.map((booking) => (
                            <tr key={booking.id}>
                              <td className="px-4 py-3 fw-bold text-primary">
                                {booking.id}
                              </td>
                              <td className="py-3">
                                {new Date(booking.date).toLocaleDateString(
                                  "vi-VN"
                                )}
                              </td>
                              <td className="py-3">{booking.table}</td>
                              <td className="py-3">{booking.duration}</td>
                              <td className="py-3 fw-bold">
                                {new Intl.NumberFormat("vi-VN", {
                                  style: "currency",
                                  currency: "VND",
                                }).format(booking.amount)}
                              </td>
                              <td className="py-3">
                                <span
                                  className={`badge ${
                                    booking.status === "completed"
                                      ? "bg-success"
                                      : booking.status === "cancelled"
                                      ? "bg-danger"
                                      : "bg-warning"
                                  }`}
                                >
                                  {booking.status === "completed"
                                    ? "Hoàn thành"
                                    : booking.status === "cancelled"
                                    ? "Đã hủy"
                                    : "Đang chờ"}
                                </span>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="6"
                              className="text-center py-5 text-muted"
                            >
                              <i className="bi bi-inbox display-4 d-block mb-3 opacity-50"></i>
                              Chưa có lịch sử đặt chỗ
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default Profile;
